import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { apiTracker } from '../lib/apiTracker';

const AUTO_RELOAD_DELAY = 3000; // 3 seconds delay before auto reload
const PENDING_REQUEST_CHECK_INTERVAL = 1000; // Check every 1 second for pending requests

// Update modes:
// 'silent' - Tự động reload ngầm, không hiển thị gì (im lặng hoàn toàn)
// 'auto' - Hiển thị banner countdown 3s rồi tự động reload (KHUYÊN DÙNG)
// 'manual' - Hiển thị banner, user phải click để reload
const UPDATE_MODE = 'auto'; // Change to 'auto' or 'manual' if needed

export function UpdateBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_RELOAD_DELAY / 1000);
  const [swState, setSwState] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);

  // Listen for API tracker changes
  useEffect(() => {
    const unsubscribe = apiTracker.onChange(() => {
      setPendingRequests(apiTracker.getPendingCount());
    });
    return unsubscribe;
  }, []);

  // Listen for Service Worker update notifications
  useEffect(() => {
    console.log('[Version] ============================================');
    console.log('[Version] UpdateBanner mounted - Service Worker mode');
    console.log('[Version] UPDATE_MODE:', UPDATE_MODE);
    console.log('[Version] Waiting for sw-update-available event...');
    console.log('[Version] ============================================');

    const handleSwUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const { newVersion, waiting, registration } = customEvent.detail;

      console.log('[Version] ============================================');
      console.log('[Version] Update received from Service Worker');
      console.log('[Version] New version:', newVersion);
      console.log('[Version] ============================================');

      // Prevent showing banner if already updating or dismissed
      if (dismissed || isUpdating) {
        console.log('[Version] Skipping update - already updating or dismissed');
        return;
      }

      setSwState({ waiting, registration, newVersion });

      if (UPDATE_MODE === 'silent') {
        // Silent mode: Reload silently without showing banner
        console.log(`[Version] [Silent Mode] Reloading silently...`);
        setIsUpdating(true);
        setTimeout(() => {
          handleUpdate({ waiting, registration });
        }, 100);
      } else if (UPDATE_MODE === 'auto') {
        // Auto mode: Show banner with countdown then auto-reload
        console.log(`[Version] [Auto Mode] Showing banner, auto-reload in ${AUTO_RELOAD_DELAY / 1000}s...`);
        setShowBanner(true);

        // Countdown timer
        let timeLeft = AUTO_RELOAD_DELAY / 1000;
        const countdownInterval = setInterval(() => {
          timeLeft--;
          setCountdown(timeLeft);
          if (timeLeft <= 0) {
            clearInterval(countdownInterval);
          }
        }, 1000);

        // Auto reload after delay, but check for pending API requests first
        const scheduleReload = () => {
          setTimeout(() => {
            if (apiTracker.hasPendingRequests()) {
              console.log(`[Version] Delaying reload - ${apiTracker.getPendingCount()} API request(s) pending`);
              // Check again after interval
              scheduleReload();
            } else {
              console.log('[Version] No pending requests, proceeding with reload');
              setIsUpdating(true);
              handleUpdate({ waiting, registration });
            }
          }, apiTracker.hasPendingRequests() ? PENDING_REQUEST_CHECK_INTERVAL : AUTO_RELOAD_DELAY);
        };

        scheduleReload();
      } else {
        // Manual mode: Show banner and wait for user action
        console.log(`[Version] [Manual Mode] Showing banner, waiting for user action...`);
        setShowBanner(true);
      }
    };

    // Listen for SW update event
    window.addEventListener('sw-update-available', handleSwUpdate);

    return () => {
      window.removeEventListener('sw-update-available', handleSwUpdate);
    };
  }, [dismissed, isUpdating]);

  const handleUpdate = (state: any = swState) => {
    console.log('[Version] Starting update process...');
    setIsUpdating(true);

    // Import activateUpdate dynamically to avoid circular dependencies
    import('@/lib/sw-registration').then(({ activateUpdate }) => {
      if (state?.waiting && state?.registration) {
        console.log('[Version] Activating waiting Service Worker...');
        activateUpdate(state.waiting, state.registration);
      } else {
        console.log('[Version] No waiting SW, falling back to hard reload');
        window.location.reload();
      }
    }).catch(err => {
      console.error('[Version] Failed to import sw-registration:', err);
      // Fallback to hard reload
      window.location.reload();
    });
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    console.log('[Version] Update dismissed by user');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-700 to-gray-800 text-white shadow-lg animate-in slide-in-from-top duration-300">
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4">
          <div className="flex items-start md:items-center gap-3 flex-1">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 md:mt-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm md:text-base">
                {UPDATE_MODE === 'auto'
                  ? pendingRequests > 0
                    ? `Đang chờ ${pendingRequests} yêu cầu hoàn tất...`
                    : `Đang cập nhật phiên bản mới... ${countdown > 0 ? countdown + 's' : ''}`
                  : 'Phiên bản mới đã sẵn sàng!'}
              </p>
              <p className="text-xs md:text-sm text-gray-200 truncate">
                {UPDATE_MODE === 'auto'
                  ? pendingRequests > 0
                    ? 'Sẽ tự động reload sau khi tạo mockup xong.'
                    : 'Trang sẽ tự động tải lại để áp dụng bản cập nhật.'
                  : 'Cập nhật để sử dụng các tính năng mới nhất và sửa lỗi.'}
              </p>
            </div>
          </div>

          {UPDATE_MODE === 'manual' && (
            <div className="flex items-center gap-2 w-full md:w-auto">
              <button
                onClick={() => handleUpdate()}
                className="flex items-center justify-center gap-2 bg-white text-slate-700 px-4 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors flex-1 md:flex-initial min-h-[44px]"
              >
                <RefreshCw className="w-4 h-4 flex-shrink-0" />
                <span>Cập nhật ngay</span>
              </button>
              <button
                onClick={handleDismiss}
                className="p-2.5 hover:bg-slate-600 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Đóng"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {UPDATE_MODE === 'auto' && (
            <div className="flex items-center gap-2">
              <div className="animate-spin">
                <RefreshCw className="w-5 h-5" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
